<template>
  <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" :modal="true" :maximizable="true"
    header="Review & Fix Errors" :style="{ width: '95vw', height: '90vh' }"
    :contentStyle="{ overflow: 'auto', padding: '0.5rem' }" position="center">
    <template #header>
      <div class="flex items-center gap-3 w-full">
        <span class="text-sm font-semibold">Review & Fix Errors</span>
        <div class="flex items-center gap-2 ml-auto mr-4">
          <Tag :value="`${validRowCount} Valid`" icon="pi pi-check-circle" severity="success" />
          <Tag :value="`${errorRowCount} Errors`" icon="pi pi-times-circle" severity="danger" />
        </div>
      </div>
    </template>

    <div v-if="errorSummary.length" class="m-2 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm" data-testid="student-import-error-summary">
      <p class="font-semibold text-orange-800 mb-1">
        <i class="pi pi-exclamation-triangle mr-1"></i>
        Most common errors
      </p>
      <ul class="text-orange-700 list-disc pl-5 space-y-0.5">
        <li v-for="item in errorSummary" :key="item.label">
          <span class="font-medium">{{ item.label }}</span> — {{ item.count }} row{{ item.count === 1 ? '' : 's' }}
          <span v-if="item.systemicHint" class="text-orange-600 italic">({{ item.systemicHint }})</span>
        </li>
      </ul>
    </div>

    <DataTable size="small" :value="studentRecords" responsiveLayout="scroll" showGridlines
      class="p-datatable-sm review-table" scrollable scrollHeight="70vh">
      <Column header="#" style="width: 50px" frozen>
        <template #body="slotProps">
          <span class="font-semibold text-gray-600">{{ slotProps.index + 1 }}</span>
        </template>
      </Column>
      <Column header="Status" style="width: 80px" frozen>
        <template #body="slotProps">
          <Tag v-if="rowErrorMap[slotProps.index]" value="Error" severity="danger" icon="pi pi-times-circle" />
          <Tag v-else value="OK" severity="success" icon="pi pi-check-circle" />
        </template>
      </Column>
      <Column v-for="field in mappedFields" :key="field.key" :header="field.label" :style="{ minWidth: '180px' }">
        <template #body="slotProps">
          <div v-tooltip.top="getFieldError(slotProps.index, field.key) || undefined">
            <Calendar
              v-if="field.key === 'dob'"
              :modelValue="parseDate(slotProps.data[field.key])"
              @update:modelValue="slotProps.data[field.key] = formatDate($event)"
              dateFormat="yy-mm-dd"
              :showIcon="true"
              :manualInput="true"
              class="w-full p-inputtext-sm"
              :class="getFieldError(slotProps.index, field.key) ? 'review-input-failed' : (rowErrorMap[slotProps.index] ? 'review-input-success' : '')" />
            <InputText
              v-else
              v-model="slotProps.data[field.key]"
              class="w-full p-inputtext-sm"
              :class="getFieldError(slotProps.index, field.key) ? 'review-input-failed' : (rowErrorMap[slotProps.index] ? 'review-input-success' : '')" />
          </div>
        </template>
      </Column>
      <Column v-if="hasSubjects" header="Subjects" :style="{ minWidth: '220px' }">
        <template #body="slotProps">
          <div v-if="subjectsPerRow?.[slotProps.index]?.length" class="flex flex-wrap gap-1">
            <Tag
              v-for="(name, i) in subjectsPerRow[slotProps.index]"
              :key="i"
              :value="name"
              severity="info"
              class="text-xs"
            />
          </div>
          <span v-else class="text-gray-400 text-xs italic">—</span>
        </template>
      </Column>
    </DataTable>

    <template #footer>
      <div class="flex justify-between w-full">
        <Button size="small" label="Close" icon="pi pi-times" class="p-button-text" @click="$emit('update:visible', false)" />
        <div class="flex gap-2">
          <Button size="small" label="Re-validate" icon="pi pi-refresh"
            class="p-button-warning" @click="$emit('revalidate')" :loading="previewingImport" />
          <Button size="small" label="Import Students" icon="pi pi-check"
            @click="$emit('import')" :loading="importingStudents"
            :disabled="errorRowCount > 0"
            v-tooltip.top="errorRowCount > 0 ? 'Fix all errors first or re-validate' : ''"
            data-testid="btn-student-import-submit-dialog" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">

const props = defineProps<{
  visible: boolean;
  studentRecords: Record<string, string>[];
  mappedFields: { key: string; label: string }[];
  rowErrorMap: Record<number, Record<string, string>>;
  errorRowCount: number;
  validRowCount: number;
  previewingImport: boolean;
  importingStudents: boolean;
  subjectsPerRow?: string[][];
}>();

defineEmits(['update:visible', 'revalidate', 'import']);

const hasSubjects = computed(() =>
  (props.subjectsPerRow || []).some((list) => list && list.length > 0),
);

function getFieldError(rowIndex: number, fieldKey: string): string {
  return props.rowErrorMap[rowIndex]?.[fieldKey] || '';
}

// Aggregate per-field error counts so users see "Gender — 100 rows" instead of
// 100 separate tooltips. Surfaces a hint when the count matches the total
// error rows, suggesting the column is unmapped rather than per-row dirty data.
const errorSummary = computed<{ key: string; label: string; count: number; systemicHint?: string }[]>(() => {
  const counts: Record<string, number> = {};
  Object.values(props.rowErrorMap).forEach((row) => {
    Object.keys(row).forEach((field) => {
      counts[field] = (counts[field] || 0) + 1;
    });
  });

  const labelFor = (key: string) =>
    props.mappedFields.find((f) => f.key === key)?.label
    ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const totalErrorRows = props.errorRowCount;

  return Object.entries(counts)
    .map(([key, count]) => ({
      key,
      label: labelFor(key),
      count,
      systemicHint: count === totalErrorRows && totalErrorRows > 1
        ? 'every error row — likely an unmapped or wrong column'
        : undefined,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
});

function parseDate(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function formatDate(date: Date | null): string {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
</script>

<style scoped>
:deep(.review-input-failed) {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
}
:deep(.review-input-failed:focus) {
  box-shadow: 0 0 0 2px #fecaca !important;
  border-color: #dc2626 !important;
}
:deep(.review-input-success) {
  border-color: #22c55e !important;
  background-color: #f0fdf4 !important;
}
:deep(.review-table .p-datatable-tbody > tr > td) {
  white-space: nowrap;
}
:deep(.review-table .p-inputtext) {
  width: 100% !important;
  min-width: 150px;
}
</style>
